import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Clock, Info } from "lucide-react";
import { useEffect, useState } from "react";
import {
  useCheckServerHealth,
  useGetDeploymentStatus,
} from "@/app/servers/api/hooks";
import type { DeploymentStatus, Server } from "@/app/servers/types";
import { getRelativeTimeString } from "@/lib/dates";

// Constants for timing
const TIME_UPDATE_INTERVAL = 1000; // 1 second
const TOOLTIP_DELAY_DURATION = 200; // milliseconds
const HEALTH_REFETCH_INTERVAL = 30_000; // 30 seconds
const ACTIVE_DEPLOYMENT_REFETCH_INTERVAL = 2000; // 2 seconds

type HealthIndicatorColumnProps = {
  server: Server;
};

// Helper function to determine status variant
const getStatusVariant = (
  error: Error | null,
  health?: { healthy: boolean; message?: string },
  deploymentStatus?: DeploymentStatus,
  isManaged?: boolean
): "success" | "destructive" | "secondary" | "warning" => {
  if (isManaged && deploymentStatus) {
    if (deploymentStatus === "running") {
      return "success";
    }
    if (deploymentStatus === "failed") {
      return "destructive";
    }
    if (deploymentStatus === "unknown") {
      return "secondary";
    }
    if (deploymentStatus === "pending" || deploymentStatus === "updating") {
      return "warning";
    }
  }

  if (error) {
    return "destructive";
  }
  if (!health) {
    return "secondary";
  }
  return health.healthy ? "success" : "destructive";
};

// Helper function to determine status text
const getStatusText = (
  error: Error | null,
  health?: { healthy: boolean; message?: string },
  deploymentStatus?: DeploymentStatus,
  isManaged?: boolean
): string => {
  if (isManaged && deploymentStatus) {
    if (deploymentStatus === "unknown") {
      return "Unknown";
    }
    if (deploymentStatus === "pending") {
      return "Deploying";
    }
    if (deploymentStatus === "updating") {
      return "Updating";
    }
    if (deploymentStatus === "running") {
      return "Running";
    }
    if (deploymentStatus === "failed") {
      return "Failed";
    }
  }

  if (error) {
    return "Error";
  }
  if (!health) {
    return "Checking...";
  }
  return health.healthy ? "Healthy" : "Unhealthy";
};

export const HealthIndicatorColumn = ({
  server,
}: HealthIndicatorColumnProps) => {
  const isManaged = server.managed;

  const {
    data: health,
    error,
    dataUpdatedAt,
  } = useCheckServerHealth({
    id: server.id,
    options: { refetchInterval: HEALTH_REFETCH_INTERVAL },
    enabled: !isManaged,
  });

  const { data: deploymentStatusData } = useGetDeploymentStatus({
    id: server.id,
    enabled: isManaged,
    activeInterval: ACTIVE_DEPLOYMENT_REFETCH_INTERVAL,
    stableInterval: HEALTH_REFETCH_INTERVAL,
  });

  const [timeString, setTimeString] = useState<string | null>(null);

  const statusVariant = getStatusVariant(
    error,
    health,
    deploymentStatusData?.status,
    isManaged
  );
  const status = getStatusText(
    error,
    health,
    deploymentStatusData?.status,
    isManaged
  );

  const shouldAnimate = isManaged
    ? deploymentStatusData?.status === "pending" ||
      deploymentStatusData?.status === "unknown" ||
      deploymentStatusData?.status === "updating"
    : !health;

  useEffect(() => {
    if (!dataUpdatedAt) {
      return;
    }

    const updateTime = () => {
      try {
        setTimeString(getRelativeTimeString(dataUpdatedAt));
      } catch {
        setTimeString(new Date(dataUpdatedAt).toLocaleTimeString());
      }
    };

    updateTime();
    const interval = setInterval(updateTime, TIME_UPDATE_INTERVAL);
    return () => clearInterval(interval);
  }, [dataUpdatedAt]);

  return (
    <TooltipProvider delay={TOOLTIP_DELAY_DURATION}>
      <Tooltip>
        <TooltipTrigger
          render={<div className="flex cursor-pointer items-center gap-2" />}
        >
          <Badge
            className={cn(
              "flex items-center gap-1.5 transition-colors",
              shouldAnimate && "animate-pulse"
            )}
            variant={statusVariant}
          >
            {status}
            <Info className="h-3 w-3 opacity-70" />
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="max-w-xs">
            <p className="font-medium">Status: {status}</p>
            {(error || (health && !health.healthy && health.message)) && (
              <div className="mt-2">
                <pre className="max-h-32 overflow-auto rounded border border-red-200 bg-red-50 p-2 text-red-800 text-xs dark:border-red-800/50 dark:bg-red-950/30 dark:text-red-300">
                  <code>
                    {health?.message || error?.message || "Unknown error"}
                  </code>
                </pre>
              </div>
            )}
            {!!timeString && (
              <div className="mt-2 flex items-center gap-1 text-muted-foreground text-xs">
                <Clock className="h-3 w-3" />
                <span>Last checked: {timeString}</span>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
